import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Formik } from 'formik';
import { Link } from 'react-router-dom';

import { StyledText } from '../../atoms/Text/StyledText';
import { StyledButton } from '../../atoms/Button/StyledButton';
import { StyledUserPanelTournament } from '../../atoms/UserPanel/StyledUserPanelTournament';
import { StyledButtonsWrapper } from '../../molecules/UserPanel/StyledButtonsWrapper';
import { StyledContentWrapper } from '../../molecules/UserPanel/StyledContentWrapper';
import { StyledButtonWrapper } from '../../molecules/UserPanel/StyledButtonWrapper';
import { StyledEditWrapper } from '../../atoms/UserPanel/StyledEditWrapper';
import { StyledContentData } from '../../molecules/UserPanel/StyledContentData';
import { StyledDataWrapper } from '../../molecules/UserPanel/StyledDataWrapper';
import { StyledResignationWrapper } from '../../../styles/sharedStyles';

import UserPanelTournamentButton from '../../molecules/UserPanelTournament/UserPanelTournamentButton';
import UserPanelTournamentScript from '../UserPanel/UserPanelTournamentScript';
import UserPanelTournamentResults from '../UserPanel/UserPanelTournamentResults';
import RegisterCompetitions from '../../molecules/Register/RegisterCompetitions';

import { getDateTimeString, getUTCDateString } from '../../../logic/eventtime.js';
import {
    updateUserRegistration,
    unregisterUserFromEvent,
    getEventScriptForCurrentUser,
    getPlayersForEvent,
} from '../../../logic/requests/events';

import { getString } from '../../../strings';

import {
    handleRegisterUpgrade,
    handlePay,
    downloadCubeComps,
    downloadContactList,
    downloadABCD,
    downloadDiplomaCards,
    downloadMedalsCards,
    downloadCupsCards,
    downloadCompetitors,
} from './UserPanelTournament.data';

import REGISTER_STATUS from '../../constants/register_status.json';
import SendPaymentReminderPopup from './SendPaymentReminderPopup.js';
import GetThanksModal from './GetThanksModal.js';
import SyncRegistrationsModal from './SyncRegistrationsModal.js';
import GrabResultsModal from './GrabResultsModal.js';
import SendGalleryAvailableNotificationPopup from './SendGalleryAvailableNotificationPopup.js';
import { canManageEvent, hasPermission } from '../../../logic/permissions.js';
import UpdatePlacesPopup from './UpdatePlacesPopup.js';
import UpdateDatesPopup from './UpdateDatesPopup.js';
import BudgetInfo from './Components/BudgetInfo.js';
import PlayersInfo from './Components/PlayersInfo.js';
import UserCompetitions from './Components/UserCompetitions.js';
import RegistrationStatus from './Components/RegistrationStatus.js';
import backendRequest from '../../../logic/requests/index.js';
import UpdateLogoPopup from './UpdateLogoPopup.js';
import GetIdsModal from './GetIdsModal.js';

const UserPanelTournament = ({
    user,
    event,
    setEvent,
    removeEvent,
    setCompetitionDeadlinePopup,
    setRegistrationClosedPopup,
    setPaymentDeadlinePopup,
    setPendingDeadlineExpiredPopup,
    allUsers,
}) => {
    class ActiveTournamentFragment {
        constructor(state) {
            this.value = state;
        }

        static NONE = new ActiveTournamentFragment(0);
        static EDIT = new ActiveTournamentFragment(1);
        static RESIGN = new ActiveTournamentFragment(2);
        static SCRIPT = new ActiveTournamentFragment(3);
        static RESULTS = new ActiveTournamentFragment(4);
    }

    const thisRef = useRef();

    const [activeTournamentFragment, setActiveTournamentFragment] = useState(
        ActiveTournamentFragment.NONE.value
    );
    const [userCompetitions, setUserCompetitions] = useState(event?.user_competitions ?? []);
    const [eventScript, seteventScript] = useState();
    const [players, setPlayers] = useState();
    const [showPaymentReminderConfirmation, setShowPaymentReminderConfirmation] = useState(false);
    const [showPhotosConfirmation, setShowPhotosConfirmation] = useState(false);
    const [showDownloadThanksModal, setShowDownloadThanksModal] = useState(false);
    const [showSyncRegistrationsModal, setShowSyncRegistrationsModal] = useState(false);
    const [showGrabResultsModal, setShowGrabResultsModal] = useState(false);
    const [showUpdatePlacesPopup, setShowUpdatePlacesPopup] = useState(false);
    const [showUpdateDatesPopup, setShowUpdateDatesPopup] = useState(false);
    const [showUpdateLogoPopup, setShowUpdateLogoPopup] = useState(false);
    const [showGetIdsPopup, setShowGetIdsPopup] = useState(false);

    const time_start = useMemo(() => getUTCDateString(new Date(event.time_start)), [event]),
        time_end = useMemo(() => getUTCDateString(new Date(event.time_end)), [event]),
        edit_initial_values = Object.fromEntries(
            new Map(userCompetitions.map((competition) => [competition, true]))
        );

    const budget = useMemo(() => {
        if (!players) return 0;
        const confirmedCompetitors = players.confirmed.length;
        const competitorsWithFreeRegistration = players.confirmed.filter((p) => p.free);
        const overallBudget =
            (confirmedCompetitors - competitorsWithFreeRegistration.length) * event.entry_fee;
        const budgetWithoutDues = overallBudget - confirmedCompetitors * event.entry_fee * 0.15;
        return {
            overallBudget: overallBudget.toFixed(2).toString(),
            budgetWithoutDues: budgetWithoutDues.toFixed(2).toString(),
        };
    }, [players, event.entry_fee]);

    const loadScenarios = () => {
        if (
            !window.confirm(
                `${getString('userPanel_userPanelTournament_czyNaPewnoChceszWczytacScenariusze')} ${
                    event.name
                }?`
            )
        )
            return;

        backendRequest('GET', `/event-management/grab-script/${event.id}`)
            .then(({ message }) =>
                alert(
                    message === 'OK'
                        ? getString('userPanel_userPanelTournament_wczytanoPomyslnie')
                        : JSON.stringify(message)
                )
            )
            .catch((err) => alert(JSON.stringify(err)));
    };

    useEffect(() => {
        if (event?.script_available && user) {
            getEventScriptForCurrentUser(event.id)
                .then((res) => {
                    // nie sprawdzamy co tu jest - backend zawsze zwróci coś dobrego, bo jak nie, to rzuca 500
                    seteventScript(res);
                })
                .catch((err) => {
                    // TODO: Pokazać coś?
                    console.error('getEventScriptForCurrentUser', err);
                });
        }
    }, [event, user]);

    const handleSubmit = (data) => {
        const competitions = [];

        Object.keys(data).forEach((key) => {
            if (data[key]) competitions.push(key);
        });

        // conajmniej jedna konkurencja jest wymagana
        if (!competitions.length) {
            // TODO: pokazać jakiś błąd?
            return;
        }

        const has_changed = !(
            competitions.length === userCompetitions.length &&
            competitions.every((element) => userCompetitions.includes(element))
        );
        if (!has_changed) {
            // brak zmian, nie mieszaj nic w bazie daynch
            setActiveTournamentFragment(ActiveTournamentFragment.NONE.value);

            return;
        }

        updateUserRegistration(event.id, competitions)
            .then((res) => {
                setActiveTournamentFragment(ActiveTournamentFragment.NONE.value);
                setUserCompetitions(competitions);
            })
            .catch((err) => {
                console.log(err);
                if (err.data.code) {
                    switch (err.data.code) {
                        case 'deadline':
                            setActiveTournamentFragment(ActiveTournamentFragment.NONE.value);
                            setEvent({
                                ...event,
                                competition_deadline_expired: 1,
                            });
                            setCompetitionDeadlinePopup?.(
                                getDateTimeString(new Date(event.competition_deadline))
                            );
                            break;
                        default:
                            break;
                    }
                }
            });
    };

    const handleRetire = () => {
        unregisterUserFromEvent(event.id)
            .then((res) => {
                setActiveTournamentFragment(ActiveTournamentFragment.NONE.value);

                const expired = res.payment_return_deadline_expired;

                if (isNaN(expired)) {
                    removeEvent(event.id);
                } else {
                    removeEvent(event.id, !res.payment_return_deadline_expired);
                }
            })
            .catch((err) => {
                if (err.data.code && err.data.code === 'deadline') {
                    setEvent({
                        ...event,
                        pending_deadline_expired: 1,
                    });
                    setPendingDeadlineExpiredPopup(
                        getDateTimeString(new Date(event.pending_deadline))
                    );
                    setActiveTournamentFragment(ActiveTournamentFragment.NONE.value);
                }
                console.error('unregisterUserFromEvent', err);
            });
    };

    const toggleActiveTournamentFragment = useCallback(
        (newFragment) =>
            setActiveTournamentFragment(
                activeTournamentFragment === newFragment
                    ? ActiveTournamentFragment.NONE.value
                    : newFragment
            ),
        [ActiveTournamentFragment.NONE.value, activeTournamentFragment, setActiveTournamentFragment]
    );

    const buttons = [
        ...(() => {
            let ret = [];

            if (event.user_register_status === REGISTER_STATUS['REJECTED']) return ret;

            if (event.results_available && !allUsers) {
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_wyniki')}
                        key={`results-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() =>
                            toggleActiveTournamentFragment(ActiveTournamentFragment.RESULTS.value)
                        }
                    />
                );
                return ret;
            }

            if (event.script_available && event.status !== 'over' && !allUsers) {
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_scenariusz')}
                        key={`script-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() =>
                            toggleActiveTournamentFragment(ActiveTournamentFragment.SCRIPT.value)
                        }
                    />
                );
            }

            if (!event.reserve && !allUsers) {
                if (event.user_register_status === REGISTER_STATUS['CONFIRMED']) {
                    if (!event.competition_deadline_expired) {
                        ret.push(
                            <UserPanelTournamentButton
                                name={getString('userPanel_userPanelTournament_zmienKonkurencje')}
                                key={`edit-button-${event.id}`}
                                color="#4E6BAA"
                                onClick={() =>
                                    toggleActiveTournamentFragment(
                                        ActiveTournamentFragment.EDIT.value
                                    )
                                }
                            />
                        );
                    }
                } else {
                    if (
                        // jeszcze nie upłynął termin
                        !event.pending_deadline_expired ||
                        event.register_status === 'open' ||
                        // rozpoczął transakcję zanim upłynął termin
                        event.user_register_status === REGISTER_STATUS['TRANSACTION_PENDING'] ||
                        // ręczny override
                        event.pending_deadline_override
                    ) {
                        ret.push(
                            event.payment_method === 'p24' ? (
                                <UserPanelTournamentButton
                                    name={
                                        event.user_register_status ===
                                        REGISTER_STATUS['TRANSACTION_PENDING']
                                            ? getString(
                                                  'userPanel_userPanelTournament_dokonczPlatnosc'
                                              )
                                            : getString('userPanel_userPanelTournament_oplacUdzial')
                                    }
                                    key={`pay-button-${event.id}`}
                                    color="#4E6BAA"
                                    onClick={() =>
                                        handlePay(
                                            event,
                                            setPaymentDeadlinePopup,
                                            setRegistrationClosedPopup,
                                            setEvent
                                        )
                                    }
                                />
                            ) : (
                                <UserPanelTournamentButton
                                    name={getString('userPanel_userPanelTournament_zatwierdz')}
                                    key={`register-upgrade-button-${event.id}`}
                                    color="#4E6BAA"
                                    onClick={() => handleRegisterUpgrade(event, setEvent)}
                                />
                            )
                        );
                    }
                }
            }

            if (!event.pending_deadline_expired && !allUsers) {
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_zrezygnuj')}
                        key={`resign-button-${event.id}`}
                        color="#E18872"
                        onClick={() =>
                            toggleActiveTournamentFragment(ActiveTournamentFragment.RESIGN.value)
                        }
                    />
                );
            }

            return ret;
        })(),
        ...(() => {
            const ret = [];

            if (canManageEvent(user, event.user_role)) {
                ret.push(
                    <UserPanelTournamentButton
                        name="CUBECOMPS"
                        key={`cubecomps-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => downloadCubeComps(event)}
                    />
                );
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_listaKontaktowa')}
                        key={`contact-list-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => downloadContactList(event)}
                    />
                );
            }

            if (allUsers && (hasPermission(user, 'can_access_all_events_page') || hasPermission(user, 'can_manage_all_events'))) {
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_zawodnicyCSV')}
                        key={`competitors-csv-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => downloadCompetitors(event)}
                    />
                );
            }

            if (allUsers && hasPermission(user, 'can_manage_all_events')) {
                // tylko admin ma ABCD
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_ABCD')}
                        key={`abcd-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => downloadABCD(event)}
                    />
                );
            }
            if (
                event.register_status === 'closed' &&
                allUsers &&
                hasPermission(user, 'can_manage_all_events')
            ) {
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_wczytajScenariusze')}
                        key={`load-scenarios-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={loadScenarios}
                    />
                );
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_sync_registrations')}
                        key={`sync-registrations-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => setShowSyncRegistrationsModal(true)}
                    />
                );
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_get_results')}
                        key={`grab-results-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => setShowGrabResultsModal(true)}
                    />
                );
            }
            if (hasPermission(user, 'can_manage_all_events') && allUsers) {
                if (event.register_status === 'open') {
                    ret.push(
                        <UserPanelTournamentButton
                            name={getString('userPanel_userPanelTournament_payment_reminder')}
                            key={`payment-reminder-button-${event.id}`}
                            color="#4E6BAA"
                            onClick={() => setShowPaymentReminderConfirmation(true)}
                        />
                    );
                    ret.push(
                        <UserPanelTournamentButton
                            name={getString('userPanel_userPanelTournament_update_places')}
                            key={`update-places-button-${event.id}`}
                            color="#4E6BAA"
                            onClick={() => setShowUpdatePlacesPopup(true)}
                        />
                    );
                    ret.push(
                        <UserPanelTournamentButton
                            name={getString('userPanel_userPanelTournament_update_dates')}
                            key={`update-dates-button-${event.id}`}
                            color="#4E6BAA"
                            onClick={() => setShowUpdateDatesPopup(true)}
                        />
                    );
                }
                if (event.gallery_available) {
                    ret.push(
                        <UserPanelTournamentButton
                            name={getString('userPanel_userPanelTournament_payment_photos')}
                            key={`photos-notification-button-${event.id}`}
                            color="#4E6BAA"
                            disabled={event.photos_email_sent > 0}
                            onClick={() => setShowPhotosConfirmation(true)}
                        />
                    );
                }
            }
            if (hasPermission(user, 'can_get_event_designs')) {
                ret.push(
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_pobierzIdentyfikatory')}
                        key={`download-id-cards-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => setShowGetIdsPopup(true)}
                    />,
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_pobierzPodziekowania')}
                        key={`download-thanks-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => setShowDownloadThanksModal(true)}
                    />,
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_pobierzDyplomy')}
                        key={`download-diploma-cards-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => downloadDiplomaCards(event)}
                    />,
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_pobierzMedale')}
                        key={`download-medals-cards-button-${event.id}`}
                        color="#4E6BAA"
                        onClick={() => downloadMedalsCards(event)}
                    />,
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_pobierzPuchary')}
                        color="#4E6BAA"
                        key={`download-cups-cards-button-${event.id}`}
                        onClick={() => downloadCupsCards(event)}
                    />,
                    <UserPanelTournamentButton
                        name={getString('userPanel_userPanelTournament_updateLogo')}
                        color="#4E6BAA"
                        key={`update-logo-button-${event.id}`}
                        onClick={() => setShowUpdateLogoPopup(true)}
                    />
                );
            }
            return ret;
        })(),
    ];

    const status_text_color = event?.reserve
        ? '#E18872'
        : {
              [REGISTER_STATUS['UNCONFIRMED']]: '#E18872',
              [REGISTER_STATUS['CONFIRMED']]: '#6AB95D',
              [REGISTER_STATUS['REJECTED']]: '#E18872',
          }[event?.user_register_status ?? REGISTER_STATUS['UNCONFIRMED']];

    useEffect(() => {
        if (!event.id) return;

        getPlayersForEvent(event.id)
            .then((res) => {
                setPlayers(res);
            })
            .catch((err) => {
                console.error('getPlayersForEvent', err);
            });
    }, [event]);

    const paymentReminderPopup = useMemo(
        () =>
            showPaymentReminderConfirmation ? (
                <SendPaymentReminderPopup
                    onClose={() => setShowPaymentReminderConfirmation(false)}
                    eventId={event.id}
                    eventName={event.name}
                />
            ) : null,
        [showPaymentReminderConfirmation, event.id, event.name]
    );

    const photosConfirmation = useMemo(
        () =>
            showPhotosConfirmation ? (
                <SendGalleryAvailableNotificationPopup
                    eventId={event.id}
                    eventName={event.name}
                    photosEmailSent={event.photos_email_sent}
                    onClose={() => setShowPhotosConfirmation(false)}
                />
            ) : null,
        [showPhotosConfirmation, event.id, event.name, event.photos_email_sent]
    );

    const getThanksPopup = useMemo(
        () =>
            showDownloadThanksModal ? (
                <GetThanksModal
                    eventData={event}
                    onClose={() => setShowDownloadThanksModal(false)}
                />
            ) : null,
        [showDownloadThanksModal, event]
    );

    const syncRegistrationsModal = useMemo(
        () =>
            showSyncRegistrationsModal ? (
                <SyncRegistrationsModal
                    eventData={event}
                    onClose={() => setShowSyncRegistrationsModal(false)}
                />
            ) : null,
        [showSyncRegistrationsModal, event]
    );

    const grabResultsModal = useMemo(
        () =>
            showGrabResultsModal ? (
                <GrabResultsModal
                    eventData={event}
                    onClose={() => setShowGrabResultsModal(false)}
                />
            ) : null,
        [showGrabResultsModal, event]
    );

    const updatePlacesPopup = useMemo(
        () =>
            showUpdatePlacesPopup ? (
                <UpdatePlacesPopup
                    eventData={event}
                    onClose={() => setShowUpdatePlacesPopup(false)}
                />
            ) : null,
        [showUpdatePlacesPopup, event]
    );

    const updateDatesPopup = useMemo(
        () =>
            showUpdateDatesPopup ? (
                <UpdateDatesPopup eventData={event} onClose={() => setShowUpdateDatesPopup(false)} />
            ) : null,
        [showUpdateDatesPopup, event]
    );

    const updateLogoPopup = useMemo(
        () =>
            showUpdateLogoPopup ? (
                <UpdateLogoPopup eventData={event} onClose={() => setShowUpdateLogoPopup(false)} />
            ) : null,
        [showUpdateLogoPopup, event]
    );

    const getIdsPopup = useMemo(
        () =>
            showGetIdsPopup ? (
                <GetIdsModal eventData={event} onClose={() => setShowGetIdsPopup(false)} />
            ) : null,
        [showGetIdsPopup, event]
    );

    return (
        <StyledUserPanelTournament>
            {paymentReminderPopup}
            {getThanksPopup}
            {syncRegistrationsModal}
            {grabResultsModal}
            {photosConfirmation}
            {updatePlacesPopup}
            {updateDatesPopup}
            {updateLogoPopup}
            {getIdsPopup}
            <StyledContentWrapper ref={thisRef}>
                <Link to={`/competition/${event.code}`}>
                    <StyledText
                        hasdeclaredfontsize="32px"
                        hasdeclaredfontweight="700"
                        hasdeclaredfontcolor="#000"
                        hasdeclaredtexttransform="uppercase"
                        hascursor="pointer"
                        as="h2"
                    >
                        {event?.name}
                    </StyledText>
                </Link>
                <StyledContentData>
                    {event?.has_logo ? (
                        <Link to={`/competition/${event.code}`}>
                            <img
                                src={`/api/events/get-logo/${event.id}/event?${new Date().getTime()}`}
                                alt="logo"
                                key={`event-logo-${showUpdateLogoPopup ? 'updated' : 'original'}`}
                            />
                        </Link>
                    ) : (
                        <></>
                    )}
                    <div>
                        <StyledDataWrapper>
                            <StyledText
                                hasdeclaredfontsize="20px"
                                hasdeclaredfontweight="600"
                                hasdeclaredfontcolor="#4E6BAA"
                            >
                                {getString('userPanel_userPanelTournament_data')}
                            </StyledText>
                            <StyledText
                                hasdeclaredfontsize="20px"
                                hasdeclaredfontweight="600"
                                hasdeclaredfontcolor="#000"
                                hasdeclaredpadding="0 0 0 8px"
                            >
                                {time_start === time_end
                                    ? time_start
                                    : `${time_start} - ${time_end}`}
                            </StyledText>
                        </StyledDataWrapper>
                        {!allUsers ? (
                            <>
                                <RegistrationStatus
                                    event={event}
                                    statusTextColor={status_text_color}
                                />
                                <UserCompetitions competitions={userCompetitions} />
                            </>
                        ) : (
                            <>
                                {hasPermission(user, 'can_manage_all_events') ? (
                                    <BudgetInfo budget={budget} />
                                ) : null}
                                <PlayersInfo players={players} event={event} />
                            </>
                        )}
                    </div>
                </StyledContentData>
            </StyledContentWrapper>
            {buttons.length > 1 ? (
                <StyledButtonsWrapper>{buttons}</StyledButtonsWrapper>
            ) : (
                <StyledButtonWrapper>{buttons}</StyledButtonWrapper>
            )}
            {(() => {
                switch (activeTournamentFragment) {
                    case ActiveTournamentFragment.RESIGN.value:
                        return (
                            <StyledResignationWrapper>
                                <StyledText
                                    hasdeclaredtexttransform="uppercase"
                                    hasdeclaredcursor="pointer"
                                    hasdeclaredtextalign="center"
                                    hasdeclaredfontsize="18px"
                                    hasdeclaredfontweight="600"
                                >
                                    {event.reserve ||
                                    !event.user_register_status ||
                                    event.user_register_status === REGISTER_STATUS['UNCONFIRMED'] ||
                                    event.payment_method !== 'p24'
                                        ? getString(
                                              'userPanel_userPanelTournament_czyNaPewnoChcesz'
                                          )
                                        : !event.payment_return_deadline_expired
                                        ? getString(
                                              'userPanel_userPanelTournament_czyNaPewnoChceszZrezygnowacPotwierdzajac'
                                          )
                                        : getString(
                                              'userPanel_userPanelTournament_czyNaPewnoChceszZrezygnowacPotwierdzajacLeczOplata'
                                          )}
                                </StyledText>
                                <div>
                                    <StyledButton
                                        hasdeclaredfontweight="bold"
                                        hasdeclaredfontsize="18px"
                                        hasdeclaredtextalign="center"
                                        hasdeclaredbgcolor="#4E6BAA"
                                        hasdeclaredfontcolor="#fff"
                                        hasdeclaredtexttransform="uppercase"
                                        hasdeclaredborderradius="25px"
                                        hasdeclaredcursor="pointer"
                                        whileHover={{ scale: 0.95 }}
                                        onClick={() =>
                                            setActiveTournamentFragment(
                                                ActiveTournamentFragment.NONE.value
                                            )
                                        }
                                    >
                                        {getString('userPanel_userPanelTournament_nie')}
                                    </StyledButton>
                                    <StyledButton
                                        hasdeclaredfontweight="bold"
                                        hasdeclaredfontsize="18px"
                                        hasdeclaredtextalign="center"
                                        hasdeclaredbgcolor="#E18872"
                                        hasdeclaredfontcolor="#fff"
                                        hasdeclaredtexttransform="uppercase"
                                        hasdeclaredborderradius="25px"
                                        hasdeclaredcursor="pointer"
                                        onClick={() => handleRetire()}
                                        className="hover-red-effect"
                                    >
                                        {getString('userPanel_userPanelTournament_tak')}
                                    </StyledButton>
                                </div>
                            </StyledResignationWrapper>
                        );
                    case ActiveTournamentFragment.EDIT.value:
                        return (
                            <Formik initialValues={edit_initial_values} onSubmit={handleSubmit}>
                                {({ handleSubmit }) => (
                                    <form onSubmit={handleSubmit}>
                                        <StyledEditWrapper>
                                            <RegisterCompetitions
                                                bordersize="6px"
                                                competitions={event?.competitions}
                                                initialValues={edit_initial_values}
                                            />
                                            <StyledButtonsWrapper>
                                                <StyledButton
                                                    hasdeclaredfontweight="bold"
                                                    hasdeclaredfontsize="18px"
                                                    hasdeclaredtextalign="center"
                                                    hasdeclaredbgcolor="#4E6BAA"
                                                    hasdeclaredfontcolor="#fff"
                                                    hasdeclaredborderradius="25px"
                                                    hasdeclaredtexttransform="uppercase"
                                                    hasdeclaredcursor="pointer"
                                                    whileHover={{ scale: 0.95 }}
                                                    onClick={() =>
                                                        setActiveTournamentFragment(
                                                            ActiveTournamentFragment.NONE.value
                                                        )
                                                    }
                                                >
                                                    {getString(
                                                        'userPanel_userPanelTournament_anuluj'
                                                    )}
                                                </StyledButton>
                                                <StyledButton
                                                    hasdeclaredfontweight="bold"
                                                    hasdeclaredfontsize="18px"
                                                    hasdeclaredtextalign="center"
                                                    hasdeclaredbgcolor="#E18872"
                                                    hasdeclaredfontcolor="#fff"
                                                    hasdeclaredborderradius="25px"
                                                    hasdeclaredtexttransform="uppercase"
                                                    hasdeclaredcursor="pointer"
                                                    className="hover-red-effect"
                                                >
                                                    {getString(
                                                        'userPanel_userPanelTournament_zapisz'
                                                    )}
                                                </StyledButton>
                                            </StyledButtonsWrapper>
                                        </StyledEditWrapper>
                                    </form>
                                )}
                            </Formik>
                        );
                    case ActiveTournamentFragment.SCRIPT.value:
                        return eventScript ? (
                            <UserPanelTournamentScript
                                user={user}
                                eventScript={eventScript}
                                close={() => {
                                    setActiveTournamentFragment(
                                        ActiveTournamentFragment.NONE.value
                                    );
                                    thisRef.current.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'nearest',
                                        inline: 'nearest',
                                    });
                                }}
                            />
                        ) : (
                            // TODO: Pokazać coś, jeśli nie ma tych danych? Albo brak WCA ID?
                            <></>
                        );
                    case ActiveTournamentFragment.RESULTS.value:
                        return (
                            <UserPanelTournamentResults
                                eventId={event.id}
                                eventCode={event.code}
                                close={() => {
                                    setActiveTournamentFragment(
                                        ActiveTournamentFragment.NONE.value
                                    );
                                    thisRef.current.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'nearest',
                                        inline: 'nearest',
                                    });
                                }}
                            />
                        );
                    case ActiveTournamentFragment.NONE.value:
                    default:
                        return <></>;
                }
            })()}
        </StyledUserPanelTournament>
    );
};

export default UserPanelTournament;
